/**
 * Product data-issue reporting — client constants and context builders.
 * Keep category/status values aligned with express_app/constants/productIssue.js.
 */

export const PRODUCT_ISSUE_STATUS = {
  REPORTED: 'reported',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
}

export const PRODUCT_ISSUE_STATUS_LABELS = {
  reported: 'Reported',
  in_progress: 'In progress',
  resolved: 'Resolved',
}

export const PRODUCT_ISSUE_CATEGORIES = [
  {
    value: 'incorrect_vat',
    label: 'Incorrect VAT',
  },
  {
    value: 'incorrect_price',
    label: 'Incorrect price',
  },
  {
    value: 'incorrect_pack_size',
    label: 'Incorrect pack size or unit',
  },
  {
    value: 'incorrect_product_information',
    label: 'Incorrect product information',
  },
  {
    value: 'other',
    label: 'Other issue',
  },
]

export const PRODUCT_ISSUE_CATEGORY_LABELS = PRODUCT_ISSUE_CATEGORIES.reduce(
  (acc, item) => {
    acc[item.value] = item.label
    return acc
  },
  {},
)

export const PRODUCT_ISSUE_SOURCE = {
  VENUE_PRODUCTS: 'venue_products',
  ORDERS: 'orders',
  ORDERED_PRODUCTS: 'ordered_products',
  INVOICES: 'invoices',
  CATALOGUE: 'catalogue',
  ADMIN_PRODUCTS: 'admin_products',
  RECEIPTING: 'receipting',
  OTHER: 'other',
}

export const PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH = 2000

function formatPrice(value) {
  if (value == null || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value)
  return `£${num.toFixed(2)}`
}

function vatLabel({ vatTreatment, hasVat } = {}) {
  if (vatTreatment === 'standard') return 'Standard-rated'
  if (vatTreatment === 'zeroRated') return 'Zero-rated'
  if (vatTreatment === 'noVat') return 'No VAT'
  if (vatTreatment) return String(vatTreatment)
  if (hasVat === true) return 'Standard-rated'
  if (hasVat === false) return 'No VAT / zero-rated'
  return ''
}

/**
 * Normalise product/order/invoice row shapes into the payload the API expects.
 */
export function buildProductIssueContext({
  product,
  order,
  invoice,
  supplier,
  supplierTitle,
  sourceScreen = PRODUCT_ISSUE_SOURCE.OTHER,
} = {}) {
  const productDoc = product?.product || product || order?.product || {}
  const orderDoc = order || {}
  const supplierDoc =
    supplier ||
    productDoc.supplier ||
    orderDoc.supplier ||
    product?.supplier ||
    {}

  const title = productDoc.title || orderDoc.title || product?.title || ''
  const sku = productDoc.sku || orderDoc.sku || ''
  const suppliersProductCode =
    productDoc.suppliersProductCode || orderDoc.suppliersProductCode || ''
  const size = productDoc.size || orderDoc.size || ''
  const uom = productDoc.UoM || orderDoc.UoM || ''
  const pricedBy = productDoc.pricedBy || orderDoc.pricedBy || ''
  const priceRaw =
    orderDoc.price ?? productDoc.price ?? product?.price ?? productDoc.OMSPrice
  const hasVat =
    orderDoc.hasVat !== undefined
      ? orderDoc.hasVat
      : productDoc.hasVat !== undefined
      ? productDoc.hasVat
      : undefined
  const vatTreatment =
    orderDoc.vatTreatment || productDoc.vatTreatment || undefined

  const productId =
    productDoc._id ||
    productDoc.id ||
    orderDoc.product?._id ||
    orderDoc.product ||
    product?._id ||
    product?.id ||
    null

  const supplierId =
    supplierDoc._id ||
    supplierDoc.id ||
    (typeof productDoc.supplier === 'string' ? productDoc.supplier : null) ||
    (typeof orderDoc.supplier === 'string' ? orderDoc.supplier : null) ||
    null

  return {
    productId: productId ? String(productId) : undefined,
    supplierId: supplierId ? String(supplierId) : undefined,
    supplierTitle:
      supplierTitle ||
      supplierDoc.title ||
      product?.supplierTitle ||
      orderDoc.supplierTitle ||
      '',
    orderId: orderDoc._id || orderDoc.id || undefined,
    invoiceId: invoice?._id || invoice?.id || undefined,
    sourceScreen,
    productSnapshot: {
      description: title,
      productCode: suppliersProductCode || sku,
      price: formatPrice(priceRaw),
      packSize: size,
      vatRate: vatLabel({ vatTreatment, hasVat }),
      sellingUnit: uom || pricedBy,
      sku,
      suppliersProductCode,
    },
  }
}

export function getProductIssueCategoryLabel(category) {
  return PRODUCT_ISSUE_CATEGORY_LABELS[category] || category || ''
}

export function getProductIssueStatusLabel(status) {
  return PRODUCT_ISSUE_STATUS_LABELS[status] || status || ''
}
